import React, { useEffect, useCallback } from 'react';
import './ConfirmationModal.css';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message?: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  isLoading?: boolean;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title = "Confirm Action",
  message = "Are you sure you want to proceed?",
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "danger",
  isLoading = false
}) => {
  // Handle keyboard navigation
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!isOpen) return;

    switch (event.key) {
      case 'Escape':
        if (!isLoading) onClose();
        break;
      case 'Enter':
        if (!isLoading) onConfirm();
        break;
      default:
        break;
    }
  }, [isOpen, onClose, onConfirm, isLoading]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="confirmation-modal-overlay" onClick={!isLoading ? onClose : undefined}>
      <div className="confirmation-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="confirmation-modal-header">
          <h3 className="confirmation-modal-title">{title}</h3>
          {!isLoading && (
            <button className="confirmation-modal-close" onClick={onClose}>
              ×
            </button>
          )}
        </div>

        {/* Body */}
        <div className="confirmation-modal-body">
          <div className="confirmation-modal-message">{message}</div>
        </div>

        {/* Footer */}
        <div className="confirmation-modal-footer">
          <button
            className="confirmation-modal-btn confirmation-modal-cancel"
            onClick={onClose}
            disabled={isLoading}
          >
            {cancelText}
          </button>
          <button
            className={`confirmation-modal-btn confirmation-modal-confirm confirmation-modal-confirm--${variant}`}
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="confirmation-modal-spinner"></span>
                Processing...
              </>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;
